import React, { useState, useEffect } from 'react';
import { Calendar, Plus, X, Check, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';

const LeaveManagement = ({ staff, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: 'vacation',
    startDate: '',
    endDate: '',
    reason: '',
    status: 'approved' // Admin can directly approve
  });

  useEffect(() => {
    if (staff?.userId) {
      loadLeaves();
    }
  }, [staff]);

  const loadLeaves = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('staff_leaves')
        .select('*')
        .eq('user_id', staff.userId)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setLeaves(data || []);
    } catch (error) {
      console.error('Error loading leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate dates
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        throw new Error('End date cannot be before start date');
      }

      // Validate staff data
      if (!staff?.userId) {
        throw new Error('Staff user ID is missing');
      }

      if (!staff?.role) {
        throw new Error('Staff role is missing');
      }

      // Map role to staff_type enum values
      const staffTypeMapping = {
        'doctor': 'doctor',
        'therapist': 'therapist',
        'receptionist': 'receptionist',
        'admin': 'admin',
        'pharmacist': 'pharmacist'
      };

      const staffType = staffTypeMapping[staff.role.toLowerCase()];
      if (!staffType) {
        throw new Error(`Invalid staff role: ${staff.role}. Must be one of: doctor, therapist, receptionist, admin, pharmacist`);
      }

      console.log('📝 Inserting leave with data:', {
        user_id: staff.userId,
        staff_type: staffType,
        leave_type: formData.leaveType,
        start_date: formData.startDate,
        end_date: formData.endDate,
        reason: formData.reason || null,
        status: formData.status
      });

      const { data, error } = await supabase
        .from('staff_leaves')
        .insert({
          user_id: staff.userId,
          staff_type: staffType,
          leave_type: formData.leaveType,
          start_date: formData.startDate,
          end_date: formData.endDate,
          reason: formData.reason || null,
          status: formData.status
        })
        .select();

      if (error) {
        console.error('❌ Database error details:', error);
        throw new Error(`Database error: ${error.message}${error.details ? ` - ${error.details}` : ''}${error.hint ? ` (Hint: ${error.hint})` : ''}`);
      }

      console.log('✅ Leave created successfully:', data);

      setShowAddForm(false);
      setFormData({
        leaveType: 'vacation',
        startDate: '',
        endDate: '',
        reason: '',
        status: 'approved'
      });
      loadLeaves();
    } catch (error) {
      console.error('❌ Error adding leave:', error);
      const errorMessage = error.message || 'Failed to add leave';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leaveId) => {
    if (!confirm('Are you sure you want to delete this leave record?')) return;

    try {
      console.log('Deleting leave with ID:', leaveId);

      const { error } = await supabase
        .from('staff_leaves')
        .delete()
        .eq('id', leaveId);

      if (error) {
        console.error('Database error details:', error);
        throw new Error(`Failed to delete leave: ${error.message}${error.details ? ` - ${error.details}` : ''}`);
      }

      console.log('Leave deleted successfully');
      loadLeaves();
    } catch (error) {
      console.error('Error deleting leave:', error);
      alert(error.message || 'Failed to delete leave');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || styles.pending;
  };

  const getLeaveTypeBadge = (type) => {
    const styles = {
      sick: 'bg-red-100 text-red-700',
      vacation: 'bg-blue-100 text-blue-700',
      emergency: 'bg-orange-100 text-orange-700',
      training: 'bg-purple-100 text-purple-700',
      personal: 'bg-gray-100 text-gray-700'
    };
    return styles[type] || styles.personal;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Leave Management</h2>
          <p className="text-gray-600 mt-1">
            Manage leave records for {staff?.fullName}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Leave
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
        </div>
      </div>

      {/* Add Leave Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Add New Leave</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type *
                </label>
                <select
                  value={formData.leaveType}
                  onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="emergency">Emergency</option>
                  <option value="training">Training</option>
                  <option value="personal">Personal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                  min={formData.startDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                placeholder="Optional reason for leave"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Leave
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Leaves List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {loading && !showAddForm ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : leaves.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No leave records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leaves.map((leave) => {
                  const startDate = new Date(leave.start_date);
                  const endDate = new Date(leave.end_date);
                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

                  return (
                    <tr key={leave.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLeaveTypeBadge(leave.leave_type)}`}>
                          {leave.leave_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(leave.start_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(leave.end_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{days}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(leave.status)}`}>
                          {leave.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {leave.reason || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDelete(leave.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete leave"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagement;

